import { WithSnackbarProps, useSnackbar, OptionsObject } from 'notistack';
import React, { ReactNode } from 'react';
import { Box, Stack, Typography, useTheme } from '@rabex-kit/rabex-ui';
import { useResponsive } from '@rabex-kit/core';
import XCircle from '../static/images/svg/alerts/XCircle.svg?react';
import Warning from '../static/images/svg/alerts/Warning.svg?react';
import SuccessWarning from '../static/images/svg/alerts/SuccessWarning.svg?react';
import InfoWarning from '../static/images/svg/alerts/InfoWarning.svg?react';
import CircleWarning from '../static/images/svg/alerts/CircleWarning.svg?react';
import ChevronLeft16 from '../static/images/svg/arrows/chevronLeft16.svg?react';
import ChevronLeft24 from '../static/images/svg/arrows/chevronLeft24.svg?react';
import WarningCircle from '../static/images/svg/alerts/WarningCircle.svg?react';

let useSnackbarRef: WithSnackbarProps;
export const SnackbarUtilsConfigurator: React.FC = () => {
  useSnackbarRef = useSnackbar();
  return null;
};

interface SnackbarContentProps {
  title?: string;
  message?: string | string[];
  hasWarningIcon?: boolean;
  hasAction?: boolean;
  actionIconLinkFunction?: () => void;
  mode?: 'success' | 'error' | 'warning' | 'info' | 'toast' | 'black';
  /**
   * Makes the whole toast a tap target. Separate from `hasAction`, which only
   * adds the chevron: a toast that leads somewhere should be clickable across
   * its surface, not just on a 13px chevron.
   */
  onClick?: () => void;
}

export const SnackbarContent: React.FC<SnackbarContentProps> = ({
  title,
  message,
  hasWarningIcon,
  hasAction,
  actionIconLinkFunction,
  mode,
  onClick
}) => {
  const { matches } = useResponsive();
  const { palette } = useTheme();
  const toastConfig = {
    success: {
      warningIcon: <SuccessWarning />,
      actionBackgroundColor: palette.success[100]
    },
    error: {
      warningIcon: <XCircle />,
      actionBackgroundColor: palette.error[100]
    },
    warning: {
      warningIcon: <Warning />,
      actionBackgroundColor: palette.warning[100]
    },
    info: {
      warningIcon: <InfoWarning />,
      actionBackgroundColor: palette.info[100]
    },
    black: {
      warningIcon: <WarningCircle />,
      actionBackgroundColor: palette.base[900]
    },
    toast: {
      warningIcon: <CircleWarning />,
      actionBackgroundColor: 'transparent'
    }
  };
  return (
    <Stack
      flexDirection="row"
      alignItems="start"
      // width={{ xs: '100%', lg: '360px' }}
      px={{ xs: 2.5, lg: 3 }}
      gap={matches({ xs: 1.5, lg: 2 })}
      onClick={onClick}
      sx={{ cursor: onClick ? 'pointer' : 'inherit' }}>
      {hasWarningIcon && mode && (
        <Box justifySelf="start" alignSelf="start">
          {toastConfig[mode].warningIcon}
        </Box>
      )}
      <Stack gap={1}>
        {title && (
          <Typography
            variant={matches({ xs: 'body3', lg: 'body2' })}
            weight="semiBold"
            color={
              !(mode === 'toast' || mode === 'black')
                ? palette.textColor[900]
                : palette.textColor[50]
            }>
            {title}
          </Typography>
        )}
        {message && (
          <Typography
            variant={matches({ xs: 'body4', lg: 'body5' })}
            color={
              !(mode === 'toast' || mode === 'black')
                ? palette.textColor[900]
                : palette.textColor[50]
            }>
            {Array.isArray(message) ? message.join(', ') : message}
          </Typography>
        )}
      </Stack>

      {hasAction && mode && (
        <Stack
          height={{ xs: 7, lg: '100%' }}
          width={{ xs: 7, lg: 13 }}
          sx={{
            borderRadius: 2,
            backgroundColor: toastConfig[mode].actionBackgroundColor
          }}
          justifySelf="end"
          alignSelf="center"
          justifyContent="center"
          alignItems="center"
          onClick={actionIconLinkFunction}>
          {/* The chevrons draw in currentColor now; the snackbar keeps the
              ink it has always had on its action pill. */}
          {matches({
            xs: <ChevronLeft16 style={{ color: '#16181F' }} />,
            lg: <ChevronLeft24 style={{ color: '#16181F' }} />
          })}
        </Stack>
      )}
    </Stack>
  );
};

const snackbarInstances = {
  success(
    msg?: string | string[],
    options: OptionsObject = {},
    title?: string,
    hasAction?: boolean,
    hasWarningIcon?: boolean,
    mode?: 'success' | 'warning' | 'error' | 'info',
    actionIconFunction?: () => void
  ) {
    this.toast(
      {
        message: msg,
        title,
        hasAction,
        hasWarningIcon,
        mode: 'warning',
        actionIconFunction
      },
      {
        ...options,
        variant: 'success',
        preventDuplicate: true,
        autoHideDuration: 3000
      }
    );
  },
  warning(
    msg?: string | string[],
    options: OptionsObject = {},
    title?: string,
    hasAction?: boolean,
    hasWarningIcon?: boolean,
    mode?: 'success' | 'warning' | 'error' | 'info',
    actionIconFunction?: () => void
  ) {
    this.toast(
      {
        message: msg,
        title,
        hasAction,
        hasWarningIcon,
        mode: 'warning',
        actionIconFunction
      },
      {
        ...options,
        variant: 'warning',
        preventDuplicate: true,
        autoHideDuration: 3000
      }
    );
  },
  info(
    msg?: string | string[],
    options: OptionsObject = {},
    title?: string,
    hasAction?: boolean,
    hasWarningIcon?: boolean,
    mode?: 'success' | 'warning' | 'error' | 'info',
    actionIconFunction?: () => void
  ) {
    this.toast(
      {
        message: msg,
        title,
        hasAction,
        hasWarningIcon,
        mode: 'info',
        actionIconFunction
      },
      {
        ...options,
        variant: 'info',
        preventDuplicate: true,
        // persist: true
        autoHideDuration: 3000
      }
    );
  },
  black(
    msg?: string | string[],
    options: OptionsObject = {},
    title?: string,
    hasAction?: boolean,
    hasWarningIcon?: boolean,
    mode?: 'success' | 'warning' | 'error' | 'info' | 'black',
    actionIconFunction?: () => void
  ) {
    this.toast(
      {
        message: msg,
        title,
        hasAction,
        hasWarningIcon,
        mode: 'black',
        actionIconFunction
      },
      {
        ...options,
        variant: 'default',
        preventDuplicate: true,
        // persist: true
        autoHideDuration: 3000
      }
    );
  },
  error(
    msg?: string | string[],
    options: OptionsObject = {},
    title?: string,
    hasAction?: boolean,
    hasWarningIcon?: boolean,
    mode?: 'success' | 'warning' | 'error' | 'info',
    actionIconFunction?: () => void
  ) {
    this.toast(
      {
        message: msg,
        title,
        hasAction,
        hasWarningIcon,
        mode: 'error',
        actionIconFunction
      },
      {
        ...options,
        variant: 'error',
        preventDuplicate: true,
        autoHideDuration: 3000
      }
    );
  },
  toast(
    content: {
      title?: string;
      message?: string | string[];
      hasAction?: boolean;
      hasWarningIcon?: boolean;
      mode?: 'success' | 'warning' | 'error' | 'info' | 'toast' | 'black';
      actionIconFunction?: () => void;
      onClick?: () => void;
    } = {},
    options: OptionsObject = {}
  ) {
    useSnackbarRef.enqueueSnackbar(
      <SnackbarContent
        title={content.title}
        message={content.message}
        mode={content.mode || 'toast'}
        hasWarningIcon={content.hasWarningIcon}
        hasAction={content.hasAction}
        actionIconLinkFunction={content.actionIconFunction}
        onClick={content.onClick}
      />,
      options
    );
  }
  // toast(msg?: string | string[], options: OptionsObject = {}) {
  //   useSnackbarRef.enqueueSnackbar(msg, options);
  // }
};

export default snackbarInstances;
